import { FC, useMemo, useState } from 'react'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import { Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import cn from './index.module.css'
import SelectTariffValue, {
  getTariffValueMin,
} from '../../../SelectTariffValue/SelectTariffValue'
import CoursePriceView from '../../../CoursePriceView/CoursePriceView'
import classNames from 'classnames'
import moment from 'moment'

interface ISelectCourseCard {
  isLoading: boolean
  courses: ICourse[]
  order: ICourseEnrollmentItem
  promoCode?: IPromoCodeDiscount
  onChange?: (
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => void
  onDismiss?: () => void | null
}

export interface ICourseEnrollmentItem {
  course: ICourse | null
  tariffId: number | null
  value: number | null
  withDemo: boolean
  enrollDate: string | null
  isLocked?: boolean
}

const SelectCourseCard: FC<ISelectCourseCard> = ({
  isLoading,
  order,
  courses,
  promoCode,
  onChange,
  onDismiss,
}) => {
  const coursesOptions = useMemo(() => {
    return courses?.map((item) => {
      return {
        value: item.id!.toString(),
        label: item?.name ?? item?.short_name ?? '',
      }
    })
  }, [courses])

  const tariff = order?.course?.directionTariffs?.find(
    (item) => item?.id?.toString() === order?.tariffId?.toString(),
  )

  const tariffsOptions = useMemo(() => {
    return (
      order?.course?.directionTariffs?.map((item) => {
        return {
          value: item.id!.toString(),
          label: item?.name ?? '',
        }
      }) ?? []
    )
  }, [order?.course])

  const onSelectCourse = (value: string | null) => {
    if (!onChange || order.isLocked) return
    onSelectTariff(null)
    if (!value) {
      onChange('course', null)
      return
    }
    onChange(
      'course',
      courses?.find((item) => item?.id?.toString() === value) ?? null,
    )
  }

  const onSelectTariff = (value: string | null) => {
    if (!onChange || order.isLocked) return

    if (!value) {
      onChange('tariffId', null)
      return
    }
    const tariff =
      order?.course?.directionTariffs?.find(
        (item) => item?.id?.toString() === value,
      ) ?? null
    onChange('value', tariff ? getTariffValueMin(tariff) : 1)
    onChange('tariffId', tariff!.id)
    onChange('withDemo', false)
  }

  const onChangeValue = (value: number | null) => {
    if (!onChange || order.isLocked) return

    onChange('value', value)
  }

  const onChangeDemo = (value: boolean | null) => {
    if (!onChange || order.isLocked) return

    onChange('withDemo', value)
  }

  if (isLoading) {
    return (
      <div className={cn.wrapper}>
        <Spin
          className={cn.loading}
          indicator={
            <LoadingOutlined
              spin
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          }
        />
      </div>
    )
  }

  const isSelected =
    order?.course !== null && order?.tariffId !== null && order.value !== null

  return (
    <div className={classNames(cn.wrapper, isSelected && cn.wrapperSelected)}>
      <SelectField
        label="Курс"
        placeholder="Выберите курс"
        value={order?.course?.id?.toString()}
        options={coursesOptions}
        onChange={(value) => onSelectCourse(value as string | null)}
        allowClear={false}
        disabled={order.isLocked}
      />
      {order?.course?.id?.toString() && (
        <SelectField
          label="Тариф"
          placeholder="Выберите тариф"
          value={order?.tariffId?.toString()}
          options={tariffsOptions}
          onChange={(value) => onSelectTariff(value as string | null)}
          allowClear={false}
          disabled={order.isLocked}
        />
      )}
      {tariff && (
        <SelectTariffValue
          tariff={tariff}
          value={order.value!}
          setValue={onChangeValue}
          demo={order?.withDemo}
          onChangeDemo={onChangeDemo}
          canManage={!order.isLocked}
        />
      )}
      {order?.enrollDate && (
        <div className={cn.enrollDateString}>
          Оплата за курс с {moment(order?.enrollDate).format('DD.MM.YYYY')}
        </div>
      )}
      <div className={cn.footerContainer}>
        {tariff && (
          <CoursePriceView
            tariff={tariff}
            value={order.value!}
            promoCode={promoCode}
            withDemo={order.withDemo}
          />
        )}
        <div className={cn.actions}>
          {onDismiss && !order.isLocked && (
            <SecondaryButton className={cn.cancelButton} onClick={onDismiss}>
              Отменить
            </SecondaryButton>
          )}
        </div>
      </div>
    </div>
  )
}

export default SelectCourseCard
